import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import { useOrg, useOrgContext } from '@/app/session';
import { orgKey } from '@/app/queryClient';
import { Button, EmptyState, ErrorState, LoadingState, Section } from '@/components/ui';
import { CorrespondenceContent, type ArchiveRow } from './CorrespondenceContent';

type Target = { companyId?: string; locationId?: string; unresolved?: true };
export function HistoricalCorrespondenceSection(target: Target) {
  const { user } = useOrgContext();
  const org = useOrg();
  if (!user || !org.hasScope('parties:read') || !org.hasScope('locations:read')) return null;
  return <History key={`${org.orgId}:${target.companyId ?? target.locationId ?? 'unresolved'}`} {...target} />;
}
function History(target: Target) {
  const { orgId } = useOrg();
  const q = useInfiniteQuery({
    queryKey: orgKey(orgId, 'historical-correspondence', target.companyId ?? '', target.locationId ?? '', target.unresolved ?? false),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) => unwrap(api.GET('/api/v1/orgs/{orgId}/historical-correspondence', {
      params: { path: { orgId }, query: { company_id: target.companyId, location_id: target.locationId, unresolved: target.unresolved, limit: 25, cursor: pageParam } }, signal,
    })),
    getNextPageParam: last => last.page.has_more ? last.page.next_cursor ?? undefined : undefined,
    staleTime: 0, gcTime: 0,
  });
  const records = new Map<string, ArchiveRow>();
  for (const row of q.data?.pages.flatMap(page => page.items) ?? []) {
    const prior = records.get(row.id);
    if (!prior || prior.version < row.version) records.set(row.id, row);
  }
  return <Section id="historical-correspondence" title={target.unresolved ? 'Unresolved historical correspondence' : 'Historical correspondence'}>
    <a href="/app/conversations/archive">Open historical archive in Conversations</a>
    <p className="muted">Private archival evidence. Only your authorized private custody is shown. Source labels are not guessed into company or site links.</p>
    {q.isPending ? <LoadingState compact label="Loading historical correspondence" /> : q.isError && !q.data ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : records.size === 0 ? <EmptyState compact title="No historical correspondence is visible here." /> : <div className="stack">
      {[...records.values()].map(row => <div className="stack" key={row.id}><CorrespondenceContent row={row} humanAllowed /><Captures row={row} /></div>)}
      {q.isError ? <ErrorState error={q.error} onRetry={() => void (q.isFetchNextPageError ? q.fetchNextPage() : q.refetch())} /> : null}
      {q.hasNextPage ? <Button size="sm" variant="quiet" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>Show more archived records</Button> : null}
    </div>}
  </Section>;
}
function Captures({ row }: { row: ArchiveRow }) {
  const { orgId, isAdmin, hasScope } = useOrg();
  const [version, setVersion] = useState<number | undefined>();
  const allowed = isAdmin && hasScope('migration:read') && hasScope('audit:content');
  const q = useQuery({
    queryKey: orgKey(orgId, 'historical-correspondence-capture', row.id, version ?? ''),
    enabled: allowed && version !== undefined,
    staleTime: 0, gcTime: 0,
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/historical-correspondence/{correspondenceId}', {
      params: { path: { orgId, correspondenceId: row.id }, query: { captured_version: version } }, signal,
    })),
  });
  if (!allowed || row.version < 2) return null;
  return <div className="stack">
    <label>Actual retained capture <select aria-label="Actual retained correspondence capture" value={version ?? ''} onChange={event => setVersion(event.target.value ? Number(event.target.value) : undefined)}>
      <option value="">Choose a capture</option>
      {Array.from({ length: row.version }, (_, i) => i + 1).reverse().map(v => <option key={v} value={v}>Capture {v}</option>)}
    </select></label>
    {version !== undefined ? q.isPending ? <LoadingState compact label="Loading archived capture" /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <CorrespondenceContent row={q.data} humanAllowed /> : null}
  </div>;
}
